import { apiDeleteRequest, apiGetRequest, apiPostRequest, apiPutRequest } from "helpers/Requests";
import axios from "./axios";
import { applyTenantScopeHeaders } from "../../context/platformAdminContext.utils";
import {
  BASE_URL,
  URL_PLATFORM_ADMIN_AUDIT_EXPORT,
  URL_PLATFORM_ADMIN_AUDIT_LOG,
  URL_PLATFORM_ADMIN_BILLING_OVERVIEW,
  URL_PLATFORM_ADMIN_BILLING_PLANS,
  URL_PLATFORM_ADMIN_BILLING_SYNC,
  URL_PLATFORM_ADMIN_BILLING_STRIPE_CUSTOMERS,
  URL_PLATFORM_ADMIN_DASHBOARD_STATS,
  URL_PLATFORM_ADMIN_SESSION,
  URL_PLATFORM_ADMIN_GHOST_END,
  URL_PLATFORM_ADMIN_GHOST_FORCE_END,
  URL_PLATFORM_ADMIN_GHOST_SESSIONS,
  URL_PLATFORM_ADMIN_GHOST_START,
  URL_PLATFORM_ADMIN_USER_LOCK,
  URL_PLATFORM_ADMIN_ROLE_CREATE,
  URL_PLATFORM_ADMIN_ROLE_STORE,
  URL_PLATFORM_ADMIN_ROLE_UPDATE,
  URL_PLATFORM_ADMIN_ROLES,
  URL_PLATFORM_ADMIN_ROLES_BASE,
  URL_PLATFORM_ADMIN_SALE_LEADS,
  URL_PLATFORM_ADMIN_SET_PASSWORD,
  URL_PLATFORM_ADMIN_STAFF_USERS,
  URL_PLATFORM_ADMIN_TENANTS,
  URL_PLATFORM_ADMIN_USAGE,
  URL_PLATFORM_ADMIN_USAGE_EXPORT,
  URL_PLATFORM_ADMIN_USAGE_REFRESH,
  URL_PLATFORM_ADMIN_AI_USAGE_OVERVIEW,
  URL_PLATFORM_ADMIN_AI_USAGE_TENANTS,
  URL_PLATFORM_ADMIN_AI_USAGE_EXPORT,
  URL_PLATFORM_ADMIN_AI_USAGE_REFRESH,
  URL_PLATFORM_ADMIN_USER_SEARCH,
  URL_PLATFORM_ADMIN_FEATURE_OF_THE_WEEK,
} from "./URL";
import {
  buildAiUsageOverviewQuery,
  buildAiUsageTenantsQuery,
  buildAiUsageTenantDetailQuery,
} from "pages/PlatformAdmin/AiUsage/aiUsageOverview.utils";
import { buildUserSearchQuery } from "pages/PlatformAdmin/Users/userSearch.utils";
import { buildProspectsListQuery } from "pages/PlatformAdmin/Prospects/prospectsList.utils";
import { buildUsageListQuery } from "pages/PlatformAdmin/Usage/usageOverview.utils";
import {
  buildVideoGalleryFromWpPages,
  FEATURE_OF_THE_WEEK_VIDEO_GALLERY_WP_API,
} from "pages/PlatformAdmin/FeatureOfTheWeek/featureOfTheWeek.utils";
import { buildGhostSessionsQuery } from "pages/PlatformAdmin/Ghost/ghostSessions.utils";
import { buildRolesListQuery } from "pages/PlatformAdmin/Roles/platformRoles.utils";

export async function fetchPlatformDashboardStats() {
  const response = await apiGetRequest(URL_PLATFORM_ADMIN_DASHBOARD_STATS);

  if (response?.status !== 200) {
    throw new Error("Failed to load platform dashboard stats");
  }

  return response?.data?.data ?? null;
}

export async function fetchPlatformSession() {
  const response = await apiGetRequest(URL_PLATFORM_ADMIN_SESSION);

  if (response?.status !== 200) {
    throw new Error(response?.data?.message || "Failed to refresh platform session");
  }

  return response?.data?.data ?? null;
}

export async function fetchPlatformSaleLeads(filters = {}) {
  const endpoint =
    typeof filters === "string"
      ? filters
      : buildProspectsListQuery(filters, URL_PLATFORM_ADMIN_SALE_LEADS);

  const response = await apiGetRequest(endpoint);

  if (response?.status !== 200) {
    throw new Error("Failed to load sale leads");
  }

  return response?.data?.data ?? null;
}

export async function createPlatformSaleLead(payload) {
  const response = await apiPostRequest(URL_PLATFORM_ADMIN_SALE_LEADS, payload);

  if (response?.status !== 201 && response?.status !== 200) {
    throw new Error(response?.data?.message || "Failed to create prospect");
  }

  return response?.data?.data ?? null;
}

export async function resendPlatformSaleLeadInvite(saleLeadUuid) {
  const response = await apiPostRequest(
    `${URL_PLATFORM_ADMIN_SALE_LEADS}/${saleLeadUuid}/resend-invite`
  );

  if (response?.status !== 200) {
    throw new Error(response?.data?.message || "Failed to resend invite");
  }

  return response?.data?.data ?? null;
}

export async function fetchPlatformTenants(endpoint, options = {}) {
  const response = await apiGetRequest(endpoint, null, options);

  if (response?.status !== 200) {
    throw new Error("Failed to load tenants");
  }

  return response?.data?.data ?? null;
}

export async function fetchPlatformTenantGeography() {
  const response = await apiGetRequest(`${URL_PLATFORM_ADMIN_TENANTS}/geography`);

  if (response?.status !== 200) {
    throw new Error("Failed to load tenant geography");
  }

  return response?.data?.data?.tenant_geography ?? null;
}

export async function fetchPlatformTenantStateGeography(stateCode) {
  const normalized = String(stateCode || "").toUpperCase();

  if (!normalized) {
    return null;
  }

  const response = await apiGetRequest(
    `${URL_PLATFORM_ADMIN_TENANTS}/geography/${normalized}`
  );

  if (response?.status !== 200) {
    throw new Error("Failed to load state geography");
  }

  return response?.data?.data?.state_geography ?? null;
}

export async function fetchPlatformTenant(tenantId) {
  const response = await apiGetRequest(`${URL_PLATFORM_ADMIN_TENANTS}/${tenantId}`);

  if (response?.status !== 200) {
    throw new Error("Failed to load tenant");
  }

  return response?.data?.data?.tenant ?? null;
}

export async function fetchPlatformTenantIntegrations(tenantId) {
  const response = await apiGetRequest(
    `${URL_PLATFORM_ADMIN_TENANTS}/${tenantId}/integrations`
  );

  if (response?.status !== 200) {
    throw new Error("Failed to load tenant integrations");
  }

  return response?.data?.data?.integration_health ?? null;
}

export async function fetchPlatformBillingOverview(section = null) {
  const url = section
    ? `${URL_PLATFORM_ADMIN_BILLING_OVERVIEW}?section=${encodeURIComponent(section)}`
    : URL_PLATFORM_ADMIN_BILLING_OVERVIEW;
  const response = await apiGetRequest(url);

  if (response?.status !== 200) {
    throw new Error("Failed to load billing overview");
  }

  return response?.data?.data?.billing ?? null;
}

export async function syncPlatformStripeInvoices() {
  const response = await apiPostRequest(URL_PLATFORM_ADMIN_BILLING_SYNC);

  if (response?.status !== 200) {
    throw new Error("Failed to sync Stripe invoices");
  }

  return response?.data?.data?.sync ?? null;
}

export async function fetchPlatformStripeCustomers(filters = {}) {
  const params = new URLSearchParams();

  if (filters.search) {
    params.set("search", filters.search);
  }

  if (filters.link_status && filters.link_status !== "all") {
    params.set("link_status", filters.link_status);
  }

  const query = params.toString();
  const endpoint = query
    ? `${URL_PLATFORM_ADMIN_BILLING_STRIPE_CUSTOMERS}?${query}`
    : URL_PLATFORM_ADMIN_BILLING_STRIPE_CUSTOMERS;
  const response = await apiGetRequest(endpoint);

  if (response?.status !== 200) {
    throw new Error("Failed to load Stripe customers");
  }

  return response?.data?.data?.stripe_customers ?? null;
}

async function postTenantAction(path, payload) {
  const response = await apiPostRequest(path, payload);

  if (response?.status !== 200) {
    throw new Error("Tenant action failed");
  }

  return response?.data?.data?.tenant ?? null;
}

export function suspendPlatformTenant(tenantId, reason) {
  return postTenantAction(`${URL_PLATFORM_ADMIN_TENANTS}/${tenantId}/suspend`, { reason });
}

export function activatePlatformTenant(tenantId, reason) {
  return postTenantAction(`${URL_PLATFORM_ADMIN_TENANTS}/${tenantId}/activate`, { reason });
}

export function setPlatformTenantReadOnly(tenantId, enabled, reason) {
  return postTenantAction(`${URL_PLATFORM_ADMIN_TENANTS}/${tenantId}/read-only`, {
    enabled,
    reason,
  });
}

export function setPlatformTenantWildcard(tenantId, enabled, reason) {
  return postTenantAction(`${URL_PLATFORM_ADMIN_TENANTS}/${tenantId}/wildcard`, {
    enabled,
    reason,
  });
}

export async function createPlatformTenantUser(tenantId, payload) {
  const response = await apiPostRequest(
    `${URL_PLATFORM_ADMIN_TENANTS}/${tenantId}/users`,
    payload
  );

  if (response?.status !== 201) {
    throw new Error("Failed to create tenant user");
  }

  return response?.data?.data?.user ?? null;
}

export async function fetchPlatformUsers(endpoint) {
  const response = await apiGetRequest(endpoint);

  if (response?.status !== 200) {
    throw new Error("Failed to load users");
  }

  return response?.data?.data ?? null;
}

export async function searchPlatformUsers(filters) {
  const endpoint =
    typeof filters === "string"
      ? buildUserSearchQuery({ email: filters })
      : buildUserSearchQuery(filters);

  return fetchPlatformUsers(endpoint);
}

export async function resetPlatformUserPassword(email, password) {
  const response = await apiPostRequest(URL_PLATFORM_ADMIN_SET_PASSWORD, {
    email,
    password,
  });

  if (response?.status !== 200) {
    throw new Error("Failed to reset password");
  }

  return response?.data?.message ?? "Password updated successfully!";
}

export async function fetchPlatformStaffUsers(endpoint) {
  const response = await apiGetRequest(endpoint);

  if (response?.status !== 200) {
    throw new Error("Failed to load platform staff");
  }

  return response?.data?.data ?? null;
}

export async function fetchPlatformAuditLog(endpoint) {
  const response = await apiGetRequest(endpoint);

  if (response?.status !== 200) {
    throw new Error("Failed to load audit log");
  }

  return response?.data?.data ?? null;
}

export async function fetchPlatformRoles(filters = { page: 1, perPage: 100 }) {
  const endpoint =
    typeof filters === "string"
      ? filters
      : buildRolesListQuery(filters, URL_PLATFORM_ADMIN_ROLES);

  const response = await apiGetRequest(endpoint);

  if (response?.status !== 200) {
    throw new Error("Failed to load platform roles");
  }

  return response?.data?.data ?? null;
}

export async function fetchPlatformRoleCreateForm() {
  const response = await apiGetRequest(URL_PLATFORM_ADMIN_ROLE_CREATE);

  if (response?.status !== 200) {
    throw new Error("Failed to load role form");
  }

  return response?.data?.data ?? null;
}

export async function fetchPlatformRoleDetail(roleId) {
  const response = await apiGetRequest(`${URL_PLATFORM_ADMIN_ROLES_BASE}/role/${roleId}`);

  if (response?.status !== 200) {
    throw new Error("Failed to load platform role");
  }

  return response?.data?.data ?? null;
}

export async function createPlatformRole(payload) {
  const response = await apiPostRequest(URL_PLATFORM_ADMIN_ROLE_STORE, payload);

  if (response?.status !== 201) {
    throw new Error("Failed to create platform role");
  }

  return response?.data?.data?.role ?? null;
}

export async function updatePlatformRole(payload) {
  const response = await apiPostRequest(URL_PLATFORM_ADMIN_ROLE_UPDATE, payload);

  if (response?.status !== 200) {
    throw new Error("Failed to update platform role");
  }

  return response?.data?.data?.role ?? null;
}

export async function deletePlatformRole(roleId) {
  const response = await apiDeleteRequest(`${URL_PLATFORM_ADMIN_ROLES_BASE}/role/${roleId}`);

  if (response?.status !== 200) {
    throw new Error("Failed to delete platform role");
  }

  return response?.data?.message ?? "Platform role deleted.";
}

export async function createPlatformStaffUser(payload) {
  const response = await apiPostRequest(URL_PLATFORM_ADMIN_STAFF_USERS, payload);

  if (response?.status !== 201) {
    throw new Error("Failed to create platform staff user");
  }

  return response?.data?.data?.staff_user ?? null;
}

export async function updatePlatformStaffUser(staffId, payload) {
  const response = await apiPutRequest(`${URL_PLATFORM_ADMIN_STAFF_USERS}/${staffId}`, payload);

  if (response?.status !== 200) {
    throw new Error("Failed to update platform staff user");
  }

  return response?.data?.data?.staff_user ?? null;
}

export async function deactivatePlatformStaffUser(staffId, reason = null) {
  const response = await apiPostRequest(
    `${URL_PLATFORM_ADMIN_STAFF_USERS}/${staffId}/deactivate`,
    reason ? { reason } : {}
  );

  if (response?.status !== 200) {
    throw new Error("Failed to deactivate platform staff user");
  }

  return response?.data?.data?.staff_user ?? null;
}

export async function startPlatformGhostSession(payload) {
  const response = await apiPostRequest(URL_PLATFORM_ADMIN_GHOST_START, payload);

  if (response?.status !== 201) {
    throw new Error(response?.data?.message || "Failed to start ghost session");
  }

  const session = response?.data?.data?.ghost_session;
  if (!session?.token) {
    throw new Error("Ghost session token missing from response");
  }

  return session;
}

export async function endPlatformGhostSession(token) {
  const response = await apiPostRequest(URL_PLATFORM_ADMIN_GHOST_END, { token });

  if (response?.status !== 200) {
    throw new Error(response?.data?.message || "Failed to end ghost session");
  }

  return response?.data?.data ?? null;
}

export async function fetchPlatformGhostSessions(filters = {}) {
  const endpoint =
    typeof filters === "string"
      ? filters
      : buildGhostSessionsQuery(filters, URL_PLATFORM_ADMIN_GHOST_SESSIONS);

  const response = await apiGetRequest(endpoint);

  if (response?.status !== 200) {
    throw new Error(response?.data?.message || "Failed to load ghost sessions");
  }

  return response?.data?.data ?? null;
}

export async function forceEndPlatformGhostSession(sessionId) {
  const response = await apiPostRequest(
    `${URL_PLATFORM_ADMIN_GHOST_FORCE_END}/${sessionId}/force-end`
  );

  if (response?.status !== 200) {
    throw new Error(response?.data?.message || "Failed to force end ghost session");
  }

  return response?.data?.data ?? null;
}

export async function activatePlatformStaffUser(staffId, reason = null) {
  const response = await apiPostRequest(
    `${URL_PLATFORM_ADMIN_STAFF_USERS}/${staffId}/activate`,
    reason ? { reason } : {}
  );

  if (response?.status !== 200) {
    throw new Error("Failed to activate platform staff user");
  }

  return response?.data?.data?.staff_user ?? null;
}

async function downloadPlatformCsv(path, filename) {
  const token = localStorage.getItem("accessToken");
  const headers = { accept: "text/csv" };
  applyTenantScopeHeaders(headers);

  const response = await axios.get(`${BASE_URL}${path}`, {
    headers: {
      ...headers,
      Authorization: token ? `Bearer ${token}` : undefined,
    },
    responseType: "blob",
  });

  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export async function fetchPlatformBillingPlans() {
  const response = await apiGetRequest(URL_PLATFORM_ADMIN_BILLING_PLANS);

  if (response?.status !== 200) {
    throw new Error("Failed to load Stripe plans");
  }

  return response?.data?.data?.plans ?? [];
}

export async function fetchPlatformTenantPaymentHistory(tenantId) {
  const response = await apiGetRequest(
    `${URL_PLATFORM_ADMIN_TENANTS}/${tenantId}/billing/payments`
  );

  if (response?.status !== 200) {
    throw new Error("Failed to load tenant payment history");
  }

  return response?.data?.data?.payment_history ?? null;
}

export async function lockPlatformUser(userId, reason) {
  const response = await apiPostRequest(`${URL_PLATFORM_ADMIN_USER_LOCK}/${userId}/lock`, { reason });

  if (response?.status !== 200) {
    throw new Error(response?.data?.message || "Failed to lock user");
  }

  return response?.data?.data?.user ?? null;
}

export async function unlockPlatformUser(userId, reason) {
  const response = await apiPostRequest(`${URL_PLATFORM_ADMIN_USER_LOCK}/${userId}/unlock`, { reason });

  if (response?.status !== 200) {
    throw new Error(response?.data?.message || "Failed to unlock user");
  }

  return response?.data?.data?.user ?? null;
}

export async function revokePlatformUserSessions(userId, reason) {
  const response = await apiPostRequest(
    `${URL_PLATFORM_ADMIN_USER_LOCK}/${userId}/revoke-sessions`,
    { reason }
  );

  if (response?.status !== 200) {
    throw new Error(response?.data?.message || "Failed to revoke sessions");
  }

  return response?.data?.data?.user ?? null;
}

export async function resetPlatformUserMfa(userId, reason) {
  const response = await apiPostRequest(`${URL_PLATFORM_ADMIN_USER_LOCK}/${userId}/mfa-reset`, { reason });

  if (response?.status !== 200) {
    throw new Error(response?.data?.message || "Failed to reset MFA");
  }

  return response?.data?.data?.user ?? null;
}

export async function updatePlatformUserEmail(userId, email, reason) {
  const response = await apiPutRequest(`${URL_PLATFORM_ADMIN_USER_LOCK}/${userId}/email`, {
    email,
    reason,
  });

  if (response?.status !== 200) {
    throw new Error(response?.data?.message || "Failed to update email");
  }

  return response?.data?.data?.user ?? null;
}

export async function cancelPlatformTenantSubscription(tenantId, reason, mode = "immediate") {
  return postTenantAction(`${URL_PLATFORM_ADMIN_TENANTS}/${tenantId}/billing/cancel-subscription`, {
    reason,
    mode,
  });
}

export async function extendPlatformTenantTrial(tenantId, reason, trialEndDate) {
  return postTenantAction(`${URL_PLATFORM_ADMIN_TENANTS}/${tenantId}/billing/extend-trial`, {
    reason,
    trial_end_date: trialEndDate,
  });
}

export async function payPlatformTenantInvoice(tenantId, invoiceId, reason) {
  const response = await apiPostRequest(
    `${URL_PLATFORM_ADMIN_TENANTS}/${tenantId}/billing/invoices/pay`,
    { invoice_id: invoiceId, reason }
  );

  if (response?.status !== 200) {
    throw new Error(response?.data?.message || "Failed to process invoice payment");
  }

  return response?.data?.data ?? null;
}

export async function sendPlatformTenantInvoiceReminder(tenantId, invoiceId, reason) {
  const payload = { invoice_id: invoiceId };
  if (reason) {
    payload.reason = reason;
  }

  const response = await apiPostRequest(
    `${URL_PLATFORM_ADMIN_TENANTS}/${tenantId}/billing/invoices/send-reminder`,
    payload
  );

  if (response?.status !== 200) {
    throw new Error(response?.data?.message || "Failed to send invoice reminder");
  }

  return response?.data?.data ?? null;
}

export async function sendPlatformTenantOutstandingReminder(tenantId, reason) {
  const payload = {};
  if (reason) {
    payload.reason = reason;
  }

  const response = await apiPostRequest(
    `${URL_PLATFORM_ADMIN_TENANTS}/${tenantId}/billing/send-outstanding-reminder`,
    payload
  );

  if (response?.status !== 200) {
    throw new Error(response?.data?.message || "Failed to send outstanding reminder");
  }

  return response?.data?.data ?? null;
}

export async function closePlatformTenantAccount(tenantId, reason) {
  return postTenantAction(`${URL_PLATFORM_ADMIN_TENANTS}/${tenantId}/billing/close-account`, {
    reason,
  });
}

export async function exportPlatformAuditLog(queryString) {
  const path = queryString
    ? `${URL_PLATFORM_ADMIN_AUDIT_EXPORT}?${queryString}`
    : URL_PLATFORM_ADMIN_AUDIT_EXPORT;
  await downloadPlatformCsv(path, "platform-audit-log.csv");
}

export async function fetchPlatformUsageHeatmap(filters = {}) {
  const endpoint =
    typeof filters === "string"
      ? filters
      : buildUsageListQuery(filters, URL_PLATFORM_ADMIN_USAGE);

  const response = await apiGetRequest(endpoint);

  if (response?.status !== 200) {
    throw new Error("Failed to load usage heatmap");
  }

  return response?.data?.data?.usage ?? null;
}

export async function fetchPlatformTenantUsage(tenantId) {
  const response = await apiGetRequest(`${URL_PLATFORM_ADMIN_TENANTS}/${tenantId}/usage`);

  if (response?.status !== 200) {
    throw new Error("Failed to load tenant usage");
  }

  return response?.data?.data?.usage ?? null;
}

export async function refreshPlatformTenantUsage(tenantId) {
  const response = await apiPostRequest(`${URL_PLATFORM_ADMIN_TENANTS}/${tenantId}/usage/refresh`);

  if (response?.status !== 200) {
    throw new Error(response?.data?.message || "Failed to refresh tenant usage");
  }

  return response?.data?.data?.usage ?? null;
}

export async function savePlatformUsageComment(tenantId, moduleKey, comment) {
  const response = await apiPutRequest(`${URL_PLATFORM_ADMIN_TENANTS}/${tenantId}/usage/comments`, {
    module_key: moduleKey,
    comment,
  });

  if (response?.status !== 200) {
    throw new Error("Failed to save usage comment");
  }

  return response?.data?.data?.comment ?? null;
}

export async function refreshAllPlatformUsage() {
  const response = await apiPostRequest(URL_PLATFORM_ADMIN_USAGE_REFRESH);

  if (response?.status !== 200) {
    throw new Error(response?.data?.message || "Failed to refresh usage snapshots");
  }

  return response?.data?.data?.refresh ?? null;
}

export async function exportPlatformUsageCsv() {
  await downloadPlatformCsv(URL_PLATFORM_ADMIN_USAGE_EXPORT, "platform-usage.csv");
}

export async function fetchPlatformAiUsageOverview(filters = {}) {
  const endpoint =
    typeof filters === "string"
      ? filters
      : buildAiUsageOverviewQuery(filters, URL_PLATFORM_ADMIN_AI_USAGE_OVERVIEW);

  const response = await apiGetRequest(endpoint);

  if (response?.status !== 200) {
    throw new Error("Failed to load AI usage overview");
  }

  return response?.data?.data?.ai_usage ?? null;
}

export async function fetchPlatformAiUsageTenants(filters = {}) {
  const endpoint =
    typeof filters === "string"
      ? filters
      : buildAiUsageTenantsQuery(filters, URL_PLATFORM_ADMIN_AI_USAGE_TENANTS);

  const response = await apiGetRequest(endpoint);

  if (response?.status !== 200) {
    throw new Error("Failed to load AI usage tenants");
  }

  return response?.data?.data?.ai_usage ?? null;
}

export async function fetchPlatformAiUsageTenant(tenantIdOrEndpoint, filters = {}) {
  const endpoint =
    typeof tenantIdOrEndpoint === "string" && tenantIdOrEndpoint.includes("super-admin/ai-usage")
      ? tenantIdOrEndpoint
      : buildAiUsageTenantDetailQuery(filters, tenantIdOrEndpoint, URL_PLATFORM_ADMIN_AI_USAGE_TENANTS);

  const response = await apiGetRequest(endpoint);

  if (response?.status !== 200) {
    throw new Error("Failed to load tenant AI usage");
  }

  return response?.data?.data?.ai_usage ?? null;
}

export async function exportPlatformAiUsageCsv(filters = {}) {
  const endpoint =
    typeof filters === "string"
      ? filters
      : buildAiUsageOverviewQuery(filters, URL_PLATFORM_ADMIN_AI_USAGE_EXPORT);

  await downloadPlatformCsv(endpoint, "platform-ai-usage.csv");
}

export async function refreshPlatformAiUsage() {
  const response = await apiPostRequest(URL_PLATFORM_ADMIN_AI_USAGE_REFRESH);

  if (response?.status !== 200) {
    throw new Error(response?.data?.message || "Failed to refresh AI usage snapshots");
  }

  return response?.data?.data?.ai_usage?.refresh ?? null;
}

export async function fetchFeatureOfTheWeek() {
  const response = await apiGetRequest(
    `${URL_PLATFORM_ADMIN_FEATURE_OF_THE_WEEK}/show`
  );

  if (response?.status !== 200) {
    throw new Error(
      response?.data?.message || "Failed to load Feature of the Week"
    );
  }

  return response?.data?.data?.feature_of_the_week ?? null;
}

export async function fetchFeatureOfTheWeekVideoGallery() {
  const response = await axios.get(FEATURE_OF_THE_WEEK_VIDEO_GALLERY_WP_API, {
    headers: { Accept: "application/json" },
  });

  if (response?.status !== 200) {
    throw new Error("Failed to load Video Gallery from vertex360.io");
  }

  return buildVideoGalleryFromWpPages(response.data);
}

export async function saveFeatureOfTheWeek(payload) {
  const endpoint = payload?.uuid
    ? `${URL_PLATFORM_ADMIN_FEATURE_OF_THE_WEEK}/update`
    : `${URL_PLATFORM_ADMIN_FEATURE_OF_THE_WEEK}/store`;

  const response = await apiPostRequest(endpoint, payload);

  if (response?.status !== 200 && response?.status !== 201) {
    const message =
      response?.data?.message ||
      response?.data?.error ||
      "Failed to save Feature of the Week";
    throw new Error(
      typeof message === "string" ? message : "Failed to save Feature of the Week"
    );
  }

  return response?.data?.data?.feature_of_the_week ?? null;
}

export async function publishFeatureOfTheWeek(uuid) {
  const response = await apiPostRequest(
    `${URL_PLATFORM_ADMIN_FEATURE_OF_THE_WEEK}/publish`,
    { uuid }
  );

  if (response?.status !== 200) {
    throw new Error(
      response?.data?.message || "Failed to publish Feature of the Week"
    );
  }

  return response?.data?.data?.feature_of_the_week ?? null;
}
